import { useCallback, useMemo } from 'react';

import { useAuthContext } from '@fhs/auth/src/hooks';
import { useDigitalGiftCardBalanceQuery } from '@fhs/payments';
import {
  CartPaymentCardType,
  ICommitDeliveryInput,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useConfigValue } from '@fhs-legacy/frontend/src/hooks/configs/use-config-value';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import {
  BasePipelineContext,
  GiftCardContext,
  PipelineStepContext,
  usePaymentMethodStore,
} from '..';
import { useTipStore } from '../../state/tip-store';
import { EncryptionService } from '../services/encryption-service';

interface GiftCardAccountData {
  accountIdentifier: string;
  cardNumber?: string;
  currentBalance?: number;
}

import useBuildPaymentProcessor from './use-build-payment-processor';

export function useBuildPipelineContext() {
  const paymentMethod = usePaymentMethodStore(s => s.selectedPaymentMethod);
  const newCardData = usePaymentMethodStore(s => s.newCardData);
  const savedCardData = usePaymentMethodStore(s => s.savedCardData);
  const creditType = usePaymentMethodStore(s => s.creditType);
  const selectedGiftCardAccountId = usePaymentMethodStore(s => s.selectedGiftCardAccountId);
  const setEncryptionService = usePaymentMethodStore(s => s.setEncryptionService);
  const userAccountsData = usePaymentMethodStore(s => s.userAccountsData);
  const { serverOrder } = useCartContext();
  const { user, updateUserInfo } = useAuthContext();
  const {
    orderPhoneNumber,
    deliveryAddress,
    deliveryInstructions,
    fireOrderIn: orderFireOrderIn,
  } = useOrderContext();
  const { store } = useStoreContext();
  const processor = useBuildPaymentProcessor();
  const appleConfig = useConfigValue({ key: 'apple', defaultValue: {}, isRegionalized: false });
  const googleConfig = useConfigValue({ key: 'google', defaultValue: {} });

  // Fetch real-time gift card balance from API instead of relying on stale DB value
  const { data: giftCardBalanceData } = useDigitalGiftCardBalanceQuery();

  const userCognitoId = (user as { cognitoId?: string } | undefined)?.cognitoId;

  const tipCents = useTipStore(s => s.totalTipCentsForCommit);
  const delivery: ICommitDeliveryInput = useMemo(() => {
    return {
      instructions: deliveryInstructions,
      tipCents,
      pushNotification: true,
      dropoff: {
        ...deliveryAddress,
        phoneNumber: orderPhoneNumber,
      },
    };
  }, [deliveryAddress, deliveryInstructions, orderPhoneNumber, tipCents]);

  const maxCartLimit = useFlag('override-checkout-limit');
  const minDeliveryAmount = useFlag('override-checkout-delivery-minimum');

  const buildPipelineContext = useCallback((): PipelineStepContext | null => {
    const rbiOrderId = serverOrder?.rbiOrderId;
    const storeId = serverOrder?.cart?.storeDetails?.storeNumber;
    const serviceMode = serverOrder?.cart?.serviceMode;
    // typed as IBackendCartEntries
    const cartEntries = serverOrder?.cart?.cartEntries;
    const fireOrderIn = orderFireOrderIn ?? 0;
    const fullName = user?.details?.name;
    // NOTE: This is an unused field on the commitOrder call, setting to false so it doesn't break
    const skipCoolingPeriod = false;
    const cartSubTotal = serverOrder?.cart?.subTotalCents ?? 0;
    const cartTotal = serverOrder?.cart?.totalCents ?? 0;

    const serverOrderStatus = serverOrder?.status ?? '';
    const hasUnavailableCartEntries = (serverOrder?.cart?.unavailableCartEntries?.length ?? 0) > 0;

    // Early return if required fields are missing
    if (!rbiOrderId || !storeId || !serviceMode || !cartEntries || !fullName) {
      return null;
    }

    const baseContext: BasePipelineContext = {
      // type error for COMET and others
      processor: processor as any,
      creditType: creditType as CartPaymentCardType,
      rbiOrderId,
      storeId,
      serviceMode,
      cartEntries,

      fireOrderIn,
      delivery,
      fullName,
      userId: userCognitoId,
      skipCoolingPeriod,

      cartTotal,
      cartSubTotal,
      serverOrderStatus,
      unavailableCartEntries: hasUnavailableCartEntries,
      maxCartLimit,
      minDeliveryAmount,

      // Pass restaurant data from cached store context
      restaurantTimezone: store?.timezone ?? undefined,
      restaurantHours: store
        ? {
            deliveryHours: store.deliveryHours,
            curbsideHours: store.curbsideHours,
            driveThruHours: store.driveThruHours,
            diningRoomHours: store.diningRoomHours,
            pickupWindowHours: store.pickupWindowHours,
            cateringHours: null, // Not available in IStore type
          }
        : undefined,

      errors: [],

      appleConfig,
      googleConfig,

      /** Called when a new card is successfully vaulted (saved); use to set it as default payment method */
      onNewCardVaulted:
        user?.details && updateUserInfo
          ? async (accountIdentifier: string) => {
              await updateUserInfo(
                {
                  ...user.details,
                  defaultCheckoutPaymentMethodId: accountIdentifier,
                  defaultReloadPaymentMethodId: accountIdentifier,
                },
                { shouldMuteUserInfoErrors: true }
              );
            }
          : undefined,
    };

    switch (paymentMethod) {
      case 'savedCard': {
        // Saved card flow requires the account identifier
        if (!savedCardData) {
          return null;
        }

        // Both Orbital and Worldpay use accountIdentifier
        const savedCardId = savedCardData.accountIdentifier;

        if (!savedCardId) {
          return null;
        }

        return {
          ...baseContext,
          paymentMethod: 'savedCard',
          savedCardId,
          last4: savedCardData.alias,
          expiryMonth: savedCardData.expiryMonth,
          expiryYear: savedCardData.expiryYear,
          // bin may not be available in savedCardData
        };
      }
      case 'applePay':
      case 'googlePay': {
        // Digital wallets don't need details upfront
        // The pipeline steps will handle requesting/validating them
        return {
          ...baseContext,
          paymentMethod,
        } as PipelineStepContext;
      }
      case 'giftCard': {
        const accounts = userAccountsData.userAccounts?.accounts ?? [];
        const giftCardAccount = selectedGiftCardAccountId
          ? accounts.find(
              (acc: any) =>
                acc?.accountIdentifier === selectedGiftCardAccountId ||
                acc?.fdAccountId === selectedGiftCardAccountId
            )
          : accounts.find((account: any) => account?.giftCard);

        if (!giftCardAccount?.giftCard) {
          return null;
        }

        const accountId =
          giftCardAccount.accountIdentifier ?? (giftCardAccount as any).fdAccountId ?? '';

        // Use real-time balance from API (no fallback to stale DB value)
        const giftCardData: GiftCardAccountData = {
          accountIdentifier: accountId,
          cardNumber: giftCardAccount.giftCard?.last4,
          currentBalance: giftCardBalanceData?.currentBalance,
        };

        if (!giftCardData.accountIdentifier) {
          return null;
        }

        return {
          ...baseContext,
          paymentMethod: 'giftCard',
          giftCardAccountId: giftCardData.accountIdentifier,
          giftCardNumber: giftCardData.cardNumber ?? '',
          giftCardBalance: giftCardData.currentBalance,
        } as GiftCardContext;
      }
      // new card flow when the paymentMethod is null
      default: {
        // Return null if card data is missing
        if (!newCardData) {
          return null;
        }

        // Create & stash the encryption service for commitOrderStep
        const encryptionService = new EncryptionService();
        setEncryptionService(encryptionService);

        return {
          ...baseContext,
          paymentMethod: 'newCard',
          cardData: newCardData,
        };
      }
    }
  }, [
    serverOrder?.rbiOrderId,
    serverOrder?.cart?.storeDetails?.storeNumber,
    serverOrder?.cart?.serviceMode,
    serverOrder?.cart?.cartEntries,
    serverOrder?.cart?.subTotalCents,
    serverOrder?.cart?.totalCents,
    serverOrder?.cart?.unavailableCartEntries?.length,
    serverOrder?.status,
    orderFireOrderIn,
    user?.details,
    processor,
    creditType,
    delivery,
    userCognitoId,
    maxCartLimit,
    minDeliveryAmount,
    store,
    appleConfig,
    googleConfig,
    updateUserInfo,
    paymentMethod,
    savedCardData,
    userAccountsData.userAccounts?.accounts,
    selectedGiftCardAccountId,
    newCardData,
    setEncryptionService,
    giftCardBalanceData,
  ]);

  return { buildPipelineContext };
}
